import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Star, CheckCircle2, Clock, Sparkles, Flame } from "lucide-react";

/**
 * Internal-only brand preview page.
 * Shows the Landing hero + product surfaces (funnel mockup, pricing block,
 * lead card) rendered three ways so the founder can pick a direction.
 *
 * Route: /brand-preview
 * Linked from nowhere — share the URL only.
 */

const OPTIONS = [
  {
    id: "a",
    name: "Heritage Navy + Marigold",
    tagline: "Pottery Barn meets Stripe — refined American premium, NOT electric blue.",
    palette: {
      bg: "#FBFAF6",
      surface: "#FFFFFF",
      ink: "#0A1F44",
      mute: "#5C6680",
      primary: "#0A1F44",
      primaryText: "#FFFFFF",
      accent: "#E8A33D",
      accentSoft: "#FBEFD8",
      hero: "#0A1F44",
    },
    notes: ["Trustworthy without being boring", "Premium home-goods feel", "Reads as 'real business'"],
  },
  {
    id: "b",
    name: "Espresso + Sage",
    tagline: "Warm 2026 wellness-brand vibe — Athletic Greens for home services.",
    palette: {
      bg: "#FBF6EE",
      surface: "#FFFFFF",
      ink: "#2A1810",
      mute: "#6B5B4F",
      primary: "#3A2418",
      primaryText: "#FBF6EE",
      accent: "#8FAD7C",
      accentSoft: "#E8F0E1",
      hero: "#3A2418",
    },
    notes: ["Most current / contemporary", "Reads premium without trying", "Zero competitor overlap"],
  },
  {
    id: "c",
    name: "Editorial Black + Marigold",
    tagline: "High-fashion magazine energy — Architectural Digest meets a SaaS app.",
    palette: {
      bg: "#F7F4EC",
      surface: "#FFFFFF",
      ink: "#0F0E11",
      mute: "#5C5860",
      primary: "#0F0E11",
      primaryText: "#FBEFD8",
      accent: "#F4B53C",
      accentSoft: "#FBEFD8",
      hero: "#0F0E11",
    },
    notes: ["Most distinctive / boldest", "Magazine-quality polish", "Marigold CTA pops hard"],
  },
];

export default function BrandPreview() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-7 flex items-start justify-between gap-6 flex-wrap">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Internal preview · Round 2</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Brand color directions</h1>
            <p className="text-slate-600 text-sm mt-2 max-w-2xl">
              Different territory this time — further from the generic home-services blue and the typical SaaS palette. Same product surfaces rendered three ways. Pick the one that feels right.
            </p>
          </div>
          <Link to="/" className="text-sm font-semibold text-slate-600 hover:text-slate-900 mt-1" data-testid="brand-back-link">← Back to live site</Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-10 space-y-12">
        {OPTIONS.map((opt) => (
          <BrandCard key={opt.id} option={opt} />
        ))}

        <div className="bg-white border border-slate-200 rounded-3xl p-7 sm:p-9">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">My pick this round</h2>
          <p className="text-slate-600 text-sm mt-2 leading-relaxed max-w-2xl">
            <strong>Option A (Heritage Navy + Marigold).</strong> It keeps the trust of blue without being electric-blue-lead-gen-site, and the marigold accent makes CTAs feel warm and premium. <strong>Option B</strong> if you want the brand to feel current and "wellness premium." <strong>Option C</strong> if you want the boldest, most-distinctive direction.
          </p>
          <p className="text-slate-500 text-xs mt-4">
            Still not landing? Tell me what's off — too dark / too warm / too corporate / too soft / wrong feeling — and I'll spin a fourth round in a totally different direction.
          </p>
        </div>
      </div>
    </div>
  );
}

function BrandCard({ option }) {
  const p = option.palette;
  return (
    <section className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm" data-testid={`brand-option-${option.id}`}>
      {/* Header strip */}
      <div className="px-7 sm:px-9 py-6 border-b border-slate-200 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="font-display text-3xl font-extrabold text-slate-900">Option {option.id.toUpperCase()}</span>
            <span className="font-display text-xl font-bold text-slate-400">·</span>
            <span className="font-display text-xl font-bold text-slate-700">{option.name}</span>
          </div>
          <p className="text-sm text-slate-600">{option.tagline}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {option.notes.map((n) => (
            <span key={n} className="text-[11px] font-semibold text-slate-600 bg-slate-100 rounded-full px-3 py-1.5">{n}</span>
          ))}
        </div>
      </div>

      {/* Swatches */}
      <div className="px-7 sm:px-9 py-5 border-b border-slate-200 flex flex-wrap gap-3">
        <Swatch label="Background" color={p.bg} />
        <Swatch label="Surface" color={p.surface} />
        <Swatch label="Ink" color={p.ink} />
        <Swatch label="Primary" color={p.primary} />
        <Swatch label="Accent" color={p.accent} />
        <Swatch label="Accent soft" color={p.accentSoft} />
      </div>

      {/* The actual mock — recreates real product surfaces */}
      <div className="p-5 sm:p-7" style={{ background: p.bg }}>
        <div className="grid lg:grid-cols-12 gap-5">
          {/* Hero / landing */}
          <div className="lg:col-span-7 rounded-2xl p-7 sm:p-9 border" style={{ background: p.surface, borderColor: "#E2E8F0" }}>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest" style={{ background: p.accentSoft, color: p.accent }}>
              <Sparkles className="w-3 h-3" /> Founder pricing — $1 for 30 days
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05] mt-4" style={{ color: p.ink }}>
              Need new window screens? <span style={{ color: p.hero }}>Get an instant quote.</span>
            </h2>
            <p className="text-sm leading-relaxed mt-3 max-w-md" style={{ color: p.mute }}>
              New screens, rescreens, and sliding doors. Real local pricing in 60 seconds.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button className="inline-flex items-center gap-1.5 h-12 px-5 rounded-2xl font-bold text-sm shadow-lg" style={{ background: p.primary, color: p.primaryText, boxShadow: `0 8px 24px -8px ${p.primary}55` }}>
                Get my quote <ArrowRight className="w-4 h-4" />
              </button>
              <button className="inline-flex items-center gap-1.5 h-12 px-5 rounded-2xl font-bold text-sm border" style={{ borderColor: "#E2E8F0", color: p.ink, background: p.surface }}>
                See how it works
              </button>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-semibold" style={{ color: p.mute }}>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" style={{ color: p.accent }} /> 60-second quote</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" style={{ color: p.accent }} /> Real local pricing</span>
              <span className="inline-flex items-center gap-1.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3" style={{ color: p.accent, fill: p.accent }} />)}</span>
            </div>
          </div>

          {/* Funnel mockup */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border overflow-hidden" style={{ background: p.surface, borderColor: "#E2E8F0" }}>
              <div className="flex items-center gap-1.5 px-3.5 py-2 border-b" style={{ background: "#F1F5F9", borderColor: "#E2E8F0" }}>
                <div className="w-2 h-2 rounded-full bg-rose-300"></div>
                <div className="w-2 h-2 rounded-full bg-amber-300"></div>
                <div className="w-2 h-2 rounded-full bg-emerald-300"></div>
                <div className="ml-3 text-[10px] font-mono text-slate-500">windowscreenquote.com/quote</div>
              </div>
              <div className="p-5">
                <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: p.hero }}>Step 4 of 4 · Almost done</div>
                <h3 className="font-display text-lg font-bold mt-1" style={{ color: p.ink }}>Window Screen Repair</h3>
                <div className="h-1.5 rounded-full mt-2 overflow-hidden" style={{ background: "#E2E8F0" }}>
                  <div className="h-full" style={{ width: "100%", background: p.primary }}></div>
                </div>
                <div className="mt-4 rounded-xl p-3.5 border" style={{ background: p.bg, borderColor: "#E2E8F0" }}>
                  <div className="text-[9px] font-bold uppercase tracking-widest" style={{ color: p.mute }}>Your quote</div>
                  <div className="font-display text-3xl font-extrabold mt-0.5" style={{ color: p.ink }}>$320 <span className="text-xs font-bold" style={{ color: p.mute }}>total</span></div>
                  <div className="text-[11px] mt-0.5" style={{ color: p.mute }}>8 window screens · Standard mesh</div>
                </div>
                <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-lg border" style={{ background: p.accentSoft, borderColor: `${p.accent}33` }}>
                  <ShieldCheck className="w-3.5 h-3.5" style={{ color: p.accent }} />
                  <div className="text-[11px] font-semibold" style={{ color: p.ink }}>Locked-in price · No phone tag</div>
                </div>
                <button className="mt-3 w-full h-11 rounded-xl text-xs font-bold inline-flex items-center justify-center gap-1.5" style={{ background: p.primary, color: p.primaryText }}>
                  I'm Ready To Book My Appointment <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Lead/Kanban card */}
          <div className="lg:col-span-5 rounded-2xl border p-5" style={{ background: p.surface, borderColor: "#E2E8F0" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: p.mute }}>New leads</div>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold" style={{ background: p.accentSoft, color: p.accent }}>
                <Flame className="w-3 h-3" /> Hot · ready to book
              </span>
            </div>
            <div className="rounded-xl border p-3.5" style={{ borderColor: `${p.accent}55`, background: `${p.accent}0A` }}>
              <div className="font-display font-bold text-sm" style={{ color: p.ink }}>Maria T. · $320</div>
              <div className="text-[11px] mt-0.5" style={{ color: p.mute }}>Phoenix, AZ · 8 screens · just now</div>
              <div className="mt-2.5 flex items-center gap-2">
                <button className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-bold" style={{ background: p.primary, color: p.primaryText }}>Call</button>
                <button className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-[11px] font-bold border" style={{ borderColor: "#E2E8F0", color: p.ink }}>Mark contacted</button>
              </div>
            </div>
            <div className="rounded-xl border p-3.5 mt-2.5" style={{ borderColor: "#E2E8F0" }}>
              <div className="font-display font-bold text-sm" style={{ color: p.ink }}>Mike C. · $612</div>
              <div className="text-[11px] mt-0.5" style={{ color: p.mute }}>Scottsdale, AZ · new sliding door · 2h ago</div>
            </div>
          </div>

          {/* Pricing card */}
          <div className="lg:col-span-7 rounded-2xl overflow-hidden border" style={{ borderColor: "#E2E8F0" }}>
            <div className="px-6 py-5 text-center" style={{ background: p.primary, color: p.primaryText }}>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-widest" style={{ background: p.accent, color: "#fff" }}>
                <Sparkles className="w-3 h-3" /> Founder offer
              </div>
              <div className="font-display text-5xl font-extrabold mt-3 leading-none">$1</div>
              <div className="font-display text-base font-bold mt-2">Try WindowScreenQuote for 30 days</div>
              <div className="text-xs opacity-80 mt-2">After trial: <strong>$97/mo</strong> · locked in for life</div>
            </div>
            <div className="p-5" style={{ background: p.surface }}>
              <ul className="space-y-1.5 text-xs" style={{ color: p.ink }}>
                <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: p.accent }} /> Your white-label quote funnel — live today</li>
                <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: p.accent }} /> Lead dashboard + Kanban + CSV export</li>
                <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: p.accent }} /> Email + text + dashboard hot-lead alerts</li>
                <li className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: p.accent }} /> Founder rate locked in forever</li>
              </ul>
              <button className="mt-4 w-full h-11 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-1.5" style={{ background: p.primary, color: p.primaryText }}>
                Start my $1 trial <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Swatch({ label, color }) {
  const isLight = ["#FFFFFF", "#FBFAF6", "#FBF6EE"].includes(color);
  return (
    <div className="flex items-center gap-2">
      <div
        className={`w-9 h-9 rounded-lg ${isLight ? "ring-1 ring-slate-200" : ""}`}
        style={{ background: color }}
        aria-hidden="true"
      />
      <div className="leading-tight">
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-[11px] font-mono text-slate-700">{color}</div>
      </div>
    </div>
  );
}
